import { useLayoutEffect, useRef, useState } from 'react';
import { cx } from './index.tsx';

export interface Bar {
  key: string;
  /** Etiqueta corta del eje (se muestran algunas, para que no se pisen). */
  label: string;
  value: number;
  /** Texto del tooltip (ej. "Lunes 6/10: $ 45.000 · 32 ventas"). */
  tooltip: string;
}

/**
 * Gráfico de barras de UNA serie (sin leyenda: el título la nombra). Barras finas con la punta
 * redondeada y apoyadas en la base, 2 px de separación, ejes discretos y el valor al pasar el
 * mouse (o con el teclado). Siempre acompañarlo con la tabla de los mismos datos.
 */
export function BarChart({
  bars,
  height = 180,
  format,
  ariaLabel,
}: {
  bars: Bar[];
  height?: number;
  format: (value: number) => string;
  ariaLabel: string;
}) {
  const [active, setActive] = useState<number | null>(null);
  // El dibujo usa el ancho real: así los textos tienen siempre el mismo tamaño en pantalla.
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(320);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    // Se mide antes de pintar, para no dibujar un gráfico más ancho que la pantalla.
    if (el.clientWidth > 0) setWidth(Math.max(200, Math.round(el.clientWidth)));
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => entry && setWidth(Math.max(200, Math.round(entry.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const top = 12;
  const bottom = 22;
  const left = 4;
  // Margen derecho para los valores de las líneas guía (no se pisan con las barras).
  const right = 64;
  const plotH = height - top - bottom;
  const max = Math.max(1, ...bars.map((b) => b.value));
  const min = Math.min(0, ...bars.map((b) => b.value));
  const range = max - min;
  const y = (v: number) => top + ((max - v) / range) * plotH;
  const step = (width - left - right) / Math.max(bars.length, 1);
  const barW = Math.max(2, Math.min(28, step - 2));
  // Etiquetas del eje: como mucho ~8, repartidas.
  const every = Math.max(1, Math.ceil(bars.length / Math.max(2, Math.floor(width / 70))));
  // Líneas guía en el máximo y la mitad (redondeadas: los importes son enteros).
  const gridValues = [Math.round(max), Math.round(max / 2)].filter((v) => v > 0);

  return (
    <div ref={box} className="relative w-full min-w-0">
      {/* Ancho 100 %: nunca ensancha la página (en el celular la corría de costado). */}
      <svg viewBox={`0 0 ${width} ${height}`} height={height} className="block w-full" role="img" aria-label={ariaLabel}>
        {gridValues.map((v) => (
          <g key={v}>
            <line x1={left} x2={width - right + 6} y1={y(v)} y2={y(v)} className="stroke-slate-200" strokeWidth={1} strokeDasharray="3 4" />
            <text x={width} y={y(v) + 4} textAnchor="end" className="fill-slate-500 text-[11px]">
              {format(v)}
            </text>
          </g>
        ))}
        <line x1={left} x2={width - right + 6} y1={y(0)} y2={y(0)} className="stroke-slate-300" strokeWidth={1} />
        {bars.map((b, i) => {
          const cx0 = left + i * step + (step - barW) / 2;
          const h = Math.abs(y(b.value) - y(0));
          const yTop = b.value >= 0 ? y(b.value) : y(0);
          const r = Math.min(4, barW / 2, h);
          // Punta redondeada del lado del dato; el lado de la base, recto.
          const path =
            b.value >= 0
              ? `M${cx0},${y(0)} V${yTop + r} Q${cx0},${yTop} ${cx0 + r},${yTop} H${cx0 + barW - r} Q${cx0 + barW},${yTop} ${cx0 + barW},${yTop + r} V${y(0)} Z`
              : `M${cx0},${y(0)} V${yTop + h - r} Q${cx0},${yTop + h} ${cx0 + r},${yTop + h} H${cx0 + barW - r} Q${cx0 + barW},${yTop + h} ${cx0 + barW},${yTop + h - r} V${y(0)} Z`;
          return (
            <g key={b.key}>
              {h > 0 && <path d={path} className={cx('chart-bar', active !== null && active !== i && 'opacity-60')} />}
              {i % every === 0 && (
                <text x={cx0 + barW / 2} y={height - 6} textAnchor="middle" className="fill-slate-500 text-[11px]">
                  {b.label}
                </text>
              )}
              {/* Zona de hover más grande que la barra. */}
              <rect
                x={left + i * step}
                y={top}
                width={step}
                height={plotH}
                fill="transparent"
                tabIndex={0}
                aria-label={b.tooltip}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                className="outline-none"
              />
            </g>
          );
        })}
      </svg>
      {active !== null && bars[active] && (
        <div
          className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs whitespace-nowrap text-white shadow"
          style={{ left: `${((left + active * step + step / 2) / width) * 100}%` }}
        >
          {bars[active].tooltip}
        </div>
      )}
    </div>
  );
}
